import React, { useEffect, useState } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { useCompany } from '../contexts/CompanyContext';
import SideBar from '../components/SideBar';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import useAuth from '../hooks/useAuth';
import { useUser } from '../contexts/UserContext';
import { Layout, Spin } from "antd";
import { app, contentPath, modules, isViewAllowed } from '../config/const';
import { useHistory } from 'react-router-dom';
import Forbidden from './Forbidden';

const { Content } = Layout;

const navOptions = [
  modules.Dashboard,
  modules.ChartOfAccounts,
  modules.JournalEntries,
  modules.TrialBalance,
  modules.GeneralLedger,
  modules.IncomeStatement,
  modules.AccountingSettings,
];

const Index = (props: any) => {
  const { config, updateConfig } = useConfig();
  const { company, updateCompany } = useCompany();
  const { updateUser } = useUser();
  const [isForbidden, setIsForbidden] = useState(false);
  const history = useHistory();

  const companyUidFromQuery = new URLSearchParams(globalThis.location.search).get('company');
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const { data: indexData } = useAuth.useIndex(tokenData, {
    session: config.session,
    company: companyUidFromQuery || company.current.uid,
    module: config.module || 'INDEX'
  });

  useEffect(() => {
    if (tokenData && indexData?.user) {
      const targetUid = companyUidFromQuery || company.current.uid;
      const defaultCompany = indexData.companies?.find((comp: any) =>
        indexData.permissions?.some((el: any) => el.key.toUpperCase().startsWith(app.Accounting) && el.company_id === comp.id) &&
        indexData.modules?.some((mod: any) => mod.app?.toUpperCase() === app.Accounting && mod.company_id === comp.id)
      ) ?? indexData.companies?.find((comp: any) =>
        indexData.permissions?.some((el: any) => el.key.toUpperCase().startsWith(app.Accounting) && el.company_id === comp.id)
      ) ?? indexData.companies?.find((comp: any) =>
        indexData.modules?.some((mod: any) => mod.app?.toUpperCase() === app.Accounting && mod.company_id === comp.id)
      ) ?? indexData.companies?.[0];

      const currentCompany = targetUid ? (indexData.companies?.find((comp: any) => comp.uid === targetUid) ?? defaultCompany) : defaultCompany;
      const currentModule = indexData.modules?.find((mod: any) => mod.app?.toUpperCase() === app.Accounting && mod.company_id === currentCompany?.id);

      if (currentCompany) {
        currentCompany.modules = indexData.modules?.filter((mod: any) => mod.company_id === currentCompany.id) || [];
        if (indexData.subscriptions) {
          currentCompany.subscription = indexData.subscriptions?.find((sub: any) => sub.company_id === currentCompany.id);
        }
      }

      updateConfig({ key: tokenData.key, module: props.module, showProgress: false });

      const userViews: string[] = (indexData.permissions || [])
        .filter((el: any) => el.type === 'VIEW' && (!currentCompany || el.company_id === currentCompany.id))
        .map((el: any) => el.key);

      const userActions: string[] = (indexData.permissions || [])
        .filter((el: any) => el.type === 'ACTION' && (!currentCompany || el.company_id === currentCompany.id))
        .map((el: any) => el.key);

      updateUser({
        ...indexData.user,
        permissions: {
          views: userViews,
          actions: userActions
        },
        roles: indexData.permissions || []
      });

      const moduleOptions = (indexData.module_options || []).filter((el: any) => (!currentModule || el.module_id === currentModule.id || !el.module_id) && el.company_id === currentCompany?.id);

      updateCompany({
        current: currentCompany,
        list: indexData.companies || [],
        moduleList: indexData.modules || [],
        currentModule: currentModule,
        moduleOptions: moduleOptions,
      });

      if (indexData?.permissions) {
        const modulePermission = indexData.permissions.some((el: any) =>
          el.key.toUpperCase().startsWith(app.Accounting) && el.company_id === currentCompany?.id
        );
        const hasModule = Boolean(currentModule);

        // Validar acceso al módulo a nivel de empresa
        if ((!modulePermission && !hasModule) || userViews.length === 0) {
          setIsForbidden(true);
        } else {
          // Validar acceso específico a la vista solicitada (props.module)
          const isCurrentViewAllowed = isViewAllowed(props.module, userViews);
          if (!isCurrentViewAllowed) {
            // Si la vista no permitida era el Dashboard inicial, redirigir automáticamente a la primera permitida
            if (props.module === modules.Dashboard) {
              const firstAvailable = navOptions.find(key => isViewAllowed(key, userViews));
              if (firstAvailable) {
                history.replace(contentPath[firstAvailable]);
                return;
              }
            }
            setIsForbidden(true);
          } else {
            setIsForbidden(false);
          }
        }
      }
    }
  }, [tokenData, indexData, config.showProgress]);

  const getPadding = () => {
    if (config.sideVisible && !config.isMobile) {
      if (config.sideCollapsed) {
        return 80;
      } else {
        return 250;
      }
    }
    if (config.sideVisible && config.isMobile) {
      return 80;
    }
    return 0;
  };

  const MainContent = isForbidden ? Forbidden : props.content;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fb" }}>
      {!isForbidden && config.sideVisible && <SideBar />}
      <Spin size="large" tip="Cargando Contabilidad..." fullscreen spinning={config.showProgress} />
      <Layout style={{ paddingLeft: !isForbidden ? getPadding() : 0, transition: "0.3s", background: "#f5f7fb" }}>
        <TopBar />
        <Content style={{
          margin: config.isMobile ? "8px 4px" : "16px",
          padding: config.isMobile ? "12px 8px" : "24px",
          background: "#fff",
          minHeight: "calc(100vh - 128px)",
          borderRadius: config.isMobile ? 6 : 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          {MainContent ? <MainContent /> : null}
        </Content>
        <Footer />
      </Layout>
    </Layout>
  );
};

export default Index;
